import type { ICommitmentData } from 'legacy/lut/services/partner360/commitment/models/apiCommitmentData'

/** Where a signer group stands: everyone signed, someone was asked and has not signed yet, or
 *  nobody was asked so far. */
export type TPlanSignerState = 'signed' | 'requested' | 'notRequested'

export interface IPlanSignerGroup {
  /** The role as the payload names it, e.g. `PBM`, `Partner`, `Manager`. */
  typeName: string
  /** Everyone in the role, in their signing order. */
  names: string[]
  signedCount: number
  totalCount: number
  state: TPlanSignerState
  /** The latest signing date, once the whole role has signed. */
  signedDate: string | null
  /** The payload's own status key of the first signer still to act, for its wording. */
  statusKey: string | null
}

const isSameValue = (left: string | undefined, right: string) =>
  (left ?? '').trim() === right.trim()

const byOrder = (left: ICommitmentData, right: ICommitmentData) =>
  (left.CommitmentOrder ?? Number.MAX_SAFE_INTEGER) -
    (right.CommitmentOrder ?? Number.MAX_SAFE_INTEGER) ||
  (left.SignerName ?? '').localeCompare(right.SignerName ?? '')

const latestDate = (dates: (string | undefined)[]) =>
  dates.reduce<string | null>(
    (latest, date) => (date && (!latest || Date.parse(date) > Date.parse(latest)) ? date : latest),
    null
  )

/**
 * The signatures one programme's plan collects, a row per role: who is in it, how many of them
 * have signed and what the role waits for. Roles keep the order the acknowledgment dialog lists
 * them in.
 */
export const getPlanSignerGroups = (
  commitments: ICommitmentData[],
  partnerId: string,
  offering: string
): IPlanSignerGroup[] => {
  const groups = new Map<string, ICommitmentData[]>()

  commitments
    .filter(
      ({ PartnerId, ProgramType }) =>
        isSameValue(PartnerId, partnerId) && isSameValue(ProgramType, offering)
    )
    .sort(byOrder)
    .forEach((item) => {
      const key = item.CommitmentTypeName ?? ''

      groups.set(key, [...(groups.get(key) ?? []), item])
    })

  return [...groups.entries()].map(([typeName, items]) => {
    const signed = items.filter(({ IsAcknowledged }) => IsAcknowledged === 1)
    const pending = items.find(({ IsAcknowledged }) => IsAcknowledged !== 1)
    const isSigned = signed.length === items.length

    return {
      typeName,
      names: items.map(({ SignerName }) => SignerName ?? '').filter(Boolean),
      signedCount: signed.length,
      totalCount: items.length,
      state: isSigned
        ? 'signed'
        : items.some(({ IsAcknowledged, EmailSentDate }) => IsAcknowledged !== 1 && EmailSentDate)
          ? 'requested'
          : 'notRequested',
      signedDate: isSigned ? latestDate(items.map(({ ActionDate }) => ActionDate)) : null,
      statusKey: pending?.CommitmentStatusKey || null,
    }
  })
}
